import React from 'react';
import { MENUS } from '@/constants';
import { baseUrlFront } from '@/routes';
import { useRegieStore } from '@/zustand';
import { MENU_VISIBILITY_RULES } from './rules';

// Attribut posé sur le conteneur du menu latéral pour scoper le CSS de masquage.
const MENU_ROOT_ATTR = 'data-menu-visibility-root';
// Id du <style> persistant injecté dans <head>.
const STYLE_ELEMENT_ID = 'menu-visibility-style';

/**
 * MenuVisibilityController
 *
 * Masque à la volée des points de menu (item, sous-item ou section dépliable entière) selon la
 * régie chargée.
 *
 * Pourquoi en « a posteriori » (CSS injecté) et pas en rendu conditionnel : le framework
 * `@mairistem/module` enregistre les menus au premier render et ne les reconstruit pas ensuite
 * (cf. Menu.js). Comme la régie est chargée APRÈS ce premier render, on ne peut pas masquer un
 * menu en ne le rendant pas — il faut agir sur le DOM déjà rendu.
 *
 * Pourquoi le <style> et le scope sont gérés impérativement et JAMAIS retirés : le framework
 * remonte tout le contenu à chaque changement de page (cf. les gardes Arrete*). Un <style> rendu
 * en JSX serait retiré puis re-rendu à chaque navigation, laissant une fenêtre sans CSS → les
 * items masqués clignotent. On injecte donc le <style> dans <head> (hors arbre React) et on ne le
 * supprime pas ; idem pour l'attribut de scope sur le menu (stable, dans le Shell).
 *
 * Pourquoi scopé au menu : les chemins des menus (ex. /listes/factures) existent AUSSI dans le
 * contenu de l'app (liens vers des factures, etc.). Un CSS global masquerait donc des éléments
 * hors menu. On marque donc le conteneur du menu (via le data-testid stable du bouton de bascule,
 * fourni par le framework) et on préfixe tous les sélecteurs par ce scope → aucune fuite possible.
 *
 * Pourquoi un sélecteur relatif pour la ligne parente : en local, la ligne d'un menu dépliable est
 * un <div> sans href ni to (cf. ShellMenuList.js) ; on la cible via sa relation au bloc de
 * sous-liens qui la suit. En déployé elle porte un `to`, déjà couvert par les sélecteurs d'attribut.
 *
 * Les règles se déclarent dans `rules.ts`. La régie est lue dans le store (null tant que non
 * chargée, ré-évaluée à chaque changement de régie).
 */
const MenuVisibilityController = () => {
  const regie = useRegieStore((state) => state.regie);

  const css = React.useMemo(() => {
    if (!regie) return '';

    const scope = `[${MENU_ROOT_ATTR}]`;

    return MENU_VISIBILITY_RULES.filter((rule) => rule.when(regie))
      .map(({ code }) => {
        // URL reconstruite comme les redirections de l'app (baseUrlFront + LOCATIONS[code]),
        // cf. ArreteAnnuelProcedureCreator — valide en local ('') comme déployé ('/facturation/regie').
        const href = `${baseUrlFront}${MENUS.LOCATIONS[code]}`;
        return [
          // Item exact + tous ses sous-items (chemin suivi d'un « / »), sans matcher un frère au
          // préfixe voisin ('/listes' n'affecte pas '/listes-autre'). Liens (href) et, en déployé,
          // parents dépliables (to).
          `${scope} [href="${href}"]`,
          `${scope} [href^="${href}/"]`,
          `${scope} [to="${href}"]`,
          `${scope} [to^="${href}/"]`,
          // Bloc des sous-liens (conteneur ayant un sous-lien pour enfant direct).
          `${scope} *:has(> [href^="${href}/"])`,
          // Ligne parente dépliable en local (sans attribut) : élément dont le frère suivant a un
          // sous-lien pour enfant direct.
          `${scope} *:has(+ * > [href^="${href}/"])`,
        ]
          .map((selector) => `${selector}{display:none!important;}`)
          .join('\n');
      })
      .join('\n');
  }, [regie]);

  // Marque le conteneur du menu (scope) : 1er ancêtre du bouton de bascule contenant plusieurs
  // liens. Idempotent et JAMAIS retiré — le menu (Shell) est stable, le retirer/reposer à chaque
  // navigation ferait clignoter les items.
  React.useLayoutEffect(() => {
    if (!regie) return;
    // Le data-testid du bouton de bascule du menu varie selon la version du framework déployée
    // ('boutonToggleMenu' en @mairistem/module 0.9.0, 'menu.toggle' sur d'autres versions). On
    // couvre les valeurs connues ; ce bouton est toujours DANS le menu, donc la remontée est sûre.
    // Si une nouvelle version change encore ce testid, l'ajouter ici.
    const toggle = document.querySelector(
      '[data-testid="boutonToggleMenu"], [data-testid="menu.toggle"]',
    );
    let menuRoot = toggle?.parentElement ?? null;
    while (menuRoot && menuRoot.querySelectorAll('a[href], [to]').length < 2) {
      menuRoot = menuRoot.parentElement;
    }
    menuRoot?.setAttribute(MENU_ROOT_ATTR, '');
  }, [regie]);

  // <style> persistant dans <head>, géré hors arbre React (survit aux remounts du composant, donc
  // pas de fenêtre sans CSS → pas de clignotement). On ne le supprime jamais ; on met juste son
  // contenu à jour quand les règles/la régie changent.
  React.useLayoutEffect(() => {
    let style = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ELEMENT_ID;
      document.head.appendChild(style);
    }
    style.textContent = css;
  }, [css]);

  return null;
};

export default MenuVisibilityController;
